import { create } from "zustand";
import { persist } from "zustand/middleware";

interface IClientOnboardingState {
  currentStep: number;
  currentSubStep: number;
  clientId: string | null;
  onNext: (() => void) | null;
  onPrevious: (() => void) | null;
  isFirstStep: boolean;
  isLastStep: boolean;
  coverageSelection: Record<number, number[]>;
  deletedStates: number[];
  coverageMode: "select" | "draw" | null;
  setCurrentStep: (step: number) => void;
  setCurrentSubStep: (subStep: number) => void;
  setClientId: (clientId: string) => void;
  setNavigationHandlers: (handlers: {
    onNext: () => void;
    onPrevious: () => void;
    isFirstStep: boolean;
    isLastStep: boolean;
  }) => void;
  setCoverageSelection: (selection: Record<number, number[]>) => void;
  updateStateCities: (stateId: number, cityIds: number[]) => void;
  clearCoverageSelection: () => void;
  markStateAsDeleted: (stateId: number) => void;
  unmarkStateAsDeleted: (stateId: number) => void;
  clearDeletedStates: () => void;
  setCoverageMode: (mode: "select" | "draw" | null) => void;

  resetOnboarding: () => void;
}

export const useClientOnboardingStore = create<IClientOnboardingState>()(
  persist(
    (set) => ({
      currentStep: 1,
      currentSubStep: 1,
      clientId: null,
      onNext: null,
      onPrevious: null,
      isFirstStep: false,
      isLastStep: false,
      coverageSelection: {},
      deletedStates: [],
      coverageMode: null,

      setCurrentStep: (step) => set({ currentStep: step }),
      setCurrentSubStep: (subStep) => set({ currentSubStep: subStep }),
      setNavigationHandlers: (handlers) =>
        set({
          onNext: handlers.onNext,
          onPrevious: handlers.onPrevious,
          isFirstStep: handlers.isFirstStep,
          isLastStep: handlers.isLastStep,
        }),
      setClientId: (clientId) => set({ clientId }),
      setCoverageSelection: (selection) => set({ coverageSelection: selection }),
      updateStateCities: (stateId, cityIds) =>
        set((state) => ({
          coverageSelection: {
            ...state.coverageSelection,
            [stateId]: cityIds,
          },
        })),
      clearCoverageSelection: () => set({ coverageSelection: {}, deletedStates: [] }),
      markStateAsDeleted: (stateId) =>
        set((state) => ({
          deletedStates: state.deletedStates.includes(stateId)
            ? state.deletedStates
            : [...state.deletedStates, stateId],
        })),
      unmarkStateAsDeleted: (stateId) =>
        set((state) => ({
          deletedStates: state.deletedStates.filter((id) => id !== stateId),
        })),
      clearDeletedStates: () => set({ deletedStates: [] }),
      setCoverageMode: (mode) => set({ coverageMode: mode }),
      resetOnboarding: () =>
        set({
          currentStep: 1,
          currentSubStep: 1,
          onNext: null,
          onPrevious: null,
          isFirstStep: false,
          isLastStep: false,
          clientId: null,
          coverageSelection: {},
          deletedStates: [],
          coverageMode: null,
        }),
    }),
    {
      name: "client-onboarding-storage",
      partialize: (state) => ({
        clientId: state.clientId,
        coverageMode: state.coverageMode,
        currentStep: state.currentStep,
        currentSubStep: state.currentSubStep,
        coverageSelection: state.coverageSelection,
        deletedStates: state.deletedStates,
      }),
    },
  ),
);
